import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Clock, DollarSign, ChevronLeft, ChevronRight, ArrowLeft,
  CalendarCheck, CheckCircle2, Loader2,
} from "lucide-react";
import { bookService, serviceSlots } from "@/lib/api";
import { toast } from "sonner";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const fmtMoney = (p) => (p === "" || p == null ? "" : `$${Number(p).toFixed(2)}`);
const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};

function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const TODAY = localYmd(new Date());

function BookingCalendar({ month, setMonth, blocked, selected, onSelect }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="rounded-xl border border-zinc-200 p-2.5" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-1.5 px-1">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-7 w-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Previous month" data-testid="booking-cal-prev"><ChevronLeft size={16} /></button>
        <span className="text-sm font-bold text-zinc-800">{month.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-7 w-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Next month" data-testid="booking-cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DOW.map((d) => <div key={d} className="text-[10px] font-semibold text-zinc-400 py-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const key = localYmd(new Date(month.getFullYear(), month.getMonth(), d));
          const isPast = key < TODAY;
          const isBlocked = blocked.has(key);
          const disabled = isPast || isBlocked;
          const isSel = selected === key;
          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => onSelect(key)}
              className={`relative h-8 rounded-lg text-xs font-medium transition-colors ${
                isSel ? "bg-violet-600 text-white" :
                disabled ? "text-zinc-300 line-through cursor-not-allowed" :
                "text-emerald-700 hover:bg-emerald-50"
              }`}
              data-testid={`booking-day-${key}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ServiceBooking({ services, blockedDates, locationId, dealerName }) {
  const [svc, setSvc] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [selected, setSelected] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [step, setStep] = useState("date"); // date | form | done
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);

  const blocked = useMemo(() => new Set(blockedDates || []), [blockedDates]);

  // Fetch available time slots whenever the date (or service) changes.
  useEffect(() => {
    setTime("");
    if (!svc || !selected) { setSlots([]); return; }
    setSlotsLoading(true);
    serviceSlots(svc.id, locationId, selected)
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [svc, selected, locationId]);

  // Next ~14 available openings (future days, not blocked).
  const openings = useMemo(() => {
    const out = [];
    const d = new Date();
    for (let i = 0; out.length < 14 && i < 180; i++) {
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
      const key = localYmd(day);
      if (!blocked.has(key)) out.push({ key, day });
    }
    return out;
  }, [blocked]);

  const pickService = (s) => { setSvc(s); setStep("date"); setSelected(""); setTime(""); };
  const reset = () => { setSvc(null); setSelected(""); setTime(""); setStep("date"); setForm({ first_name: "", last_name: "", email: "", phone: "", note: "" }); };

  const confirm = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) { toast.error("Please enter your name"); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Please enter an email or phone"); return; }
    setBusy(true);
    try {
      await bookService({
        service_id: svc.id, location_id: locationId, date: selected, time,
        ...form,
      });
      setStep("done");
      toast.success("Your booking is confirmed! 🎉");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="service-booking-panel">
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-100">Book a Service</div>
        <h3 className="text-sm font-bold mt-0.5 inline-flex items-center gap-1.5"><Sparkles size={15} /> {dealerName ? `${dealerName}'s Services` : "Available Services"}</h3>
      </div>

      <div className="p-3">
        {/* Service list */}
        {!svc && (
          <div className="space-y-2" data-testid="booking-services-list">
            {services.map((s) => (
              <button key={s.id} onClick={() => pickService(s)} className="w-full text-left rounded-xl border border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50/40 p-3 transition-colors" data-testid={`booking-service-${s.id}`}>
                <div className="font-bold text-zinc-900 text-sm">{s.name}</div>
                {s.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{s.description}</div>}
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1.5">
                  {s.duration && <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.duration} min</span>}
                  {s.price !== "" && s.price != null && <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><DollarSign size={12} /> {fmtMoney(s.price)}</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Date / openings */}
        {svc && step === "date" && (
          <div data-testid="booking-date-step">
            <button onClick={reset} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline mb-2" data-testid="booking-back-services"><ArrowLeft size={13} /> All services</button>
            <div className="rounded-xl bg-zinc-50 px-3 py-2 mb-3">
              <div className="font-bold text-zinc-900 text-sm">{svc.name}</div>
              <div className="text-xs text-zinc-500">{[svc.duration && `${svc.duration} min`, svc.price !== "" && svc.price != null && fmtMoney(svc.price)].filter(Boolean).join(" · ")}</div>
            </div>

            <p className="text-xs font-semibold text-zinc-600 mb-1.5">Pick an opening</p>
            <BookingCalendar month={month} setMonth={setMonth} blocked={blocked} selected={selected} onSelect={setSelected} />

            <p className="text-[11px] font-semibold text-zinc-500 mt-3 mb-1.5">Next openings</p>
            <div className="flex flex-wrap gap-1.5" data-testid="booking-openings">
              {openings.map((o) => {
                const isSel = selected === o.key;
                return (
                  <button key={o.key} onClick={() => { setSelected(o.key); setMonth(new Date(o.day.getFullYear(), o.day.getMonth(), 1)); }} className={`text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors ${isSel ? "bg-violet-600 text-white border-violet-600" : "border-zinc-200 text-zinc-600 hover:border-emerald-300"}`} data-testid={`opening-${o.key}`}>
                    {o.day.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </button>
                );
              })}
            </div>

            {selected && (
              <div className="mt-4" data-testid="booking-times">
                <p className="text-xs font-semibold text-zinc-600 mb-1.5 inline-flex items-center gap-1.5">
                  <Clock size={13} /> Pick a time
                  <span className="text-zinc-400 font-normal">— {new Date(selected + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                </p>
                {slotsLoading ? (
                  <div className="py-4 text-center text-zinc-400"><Loader2 className="animate-spin inline" size={16} /></div>
                ) : slots.length === 0 ? (
                  <p className="text-xs text-rose-500 py-2" data-testid="booking-no-slots">No openings on this day. Please pick another date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setTime(s)} className={`text-xs font-semibold rounded-lg py-1.5 border transition-colors ${time === s ? "bg-violet-600 text-white border-violet-600" : "border-zinc-200 text-zinc-700 hover:border-emerald-300"}`} data-testid={`slot-${s}`}>
                        {fmtTime(s)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button disabled={!selected || !time} onClick={() => setStep("form")} className="w-full mt-4 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-bold transition-colors" data-testid="booking-continue-btn">
              {selected && time ? `Continue — ${new Date(selected + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${fmtTime(time)}` : "Select a date & time"}
            </button>
          </div>
        )}

        {/* Contact form */}
        {svc && step === "form" && (
          <div className="space-y-2.5" data-testid="booking-form-step">
            <button onClick={() => setStep("date")} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline" data-testid="booking-back-date"><ArrowLeft size={13} /> Change date</button>
            <div className="rounded-xl bg-zinc-50 px-3 py-2">
              <div className="font-bold text-zinc-900 text-sm">{svc.name}</div>
              <div className="text-xs text-violet-700 font-semibold mt-0.5 inline-flex items-center gap-1"><CalendarCheck size={13} /> {new Date(selected + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {fmtTime(time)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className="h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="booking-first" />
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" className="h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="booking-last" />
            </div>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="booking-email" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" placeholder="Mobile phone" className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm" data-testid="booking-phone" />
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Anything we should know? (optional)" className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" data-testid="booking-note" />
            <button disabled={busy} onClick={confirm} className="w-full h-11 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="booking-confirm-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirm Booking"}
            </button>
          </div>
        )}

        {/* Done */}
        {svc && step === "done" && (
          <div className="text-center py-6" data-testid="booking-done">
            <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
            <p className="font-bold text-emerald-800 mt-2">You're booked!</p>
            <p className="text-xs text-zinc-500 mt-1">{svc.name} · {new Date(selected + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" })} at {fmtTime(time)}</p>
            <button onClick={reset} className="mt-4 text-sm font-semibold text-violet-700 hover:underline" data-testid="booking-another">Book another service</button>
          </div>
        )}
      </div>
    </div>
  );
}
